import { style, globalStyle } from '@vanilla-extract/css';
import { recipe } from '@vanilla-extract/recipes';
import { vars } from '../../theme/contract.css';
import { mobile, reducedMotion } from '../../theme/media';

// ============================================
// TYPES
// ============================================

export type TextareaVariant = 'default' | 'error' | 'success';
export type TextareaSize = 'xs' | 'sm' | 'md' | 'lg';
export type TextareaResize = 'none' | 'vertical' | 'both';

// ============================================
// CONTAINER & LABEL
// ============================================

export const container = style({
  display: 'inline-flex',
  flexDirection: 'column',
  gap: vars.spacing.gap.sm,
});

export const containerFullWidth = style({
  width: '100%',
});

/** 밑줄은 라벨을 긋는다 — 근거는 `Input.css.ts` 의 같은 자리. */
export const label = style({
  fontFamily: vars.typography.fontFamily.primary,
  fontSize: vars.typography.fontSize.sm,
  fontWeight: vars.typography.fontWeight.semibold,
  color: vars.bright.primary.light,
  letterSpacing: vars.typography.letterSpacing.wide,
  position: 'relative',
  alignSelf: 'flex-start',
  paddingBottom: '4px',
  paddingRight: '14px',
});

export const labelUnderline = style({
  position: 'absolute',
  bottom: 0,
  left: 0,
  width: '100%',
  height: '1px',
  background: `linear-gradient(90deg, ${vars.bright.primary.default} 0%, ${vars.bright.secondary.default} 50%, transparent 100%)`,
});

// ============================================
// WRAPPER
// ============================================

export const wrapper = style({
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
});

export const wrapperFullWidth = style({
  width: '100%',
});

// ============================================
// TEXTAREA BASE & RECIPE
// ============================================

const textareaBase = {
  fontFamily: vars.typography.fontFamily.primary,
  fontWeight: vars.typography.fontWeight.regular,
  color: vars.typography.textColor.primary,
  letterSpacing: vars.typography.letterSpacing.wide,
  background: vars.form.fieldGradient,
  border: `1px solid ${vars.surface.border.primary}`,
  borderRadius: vars.spacing.borderRadius.md,
  outline: 'none',
  transition: `border-color ${vars.animation.duration.normal} ${vars.animation.easing.easeOut}, box-shadow ${vars.animation.duration.normal} ${vars.animation.easing.easeOut}`,
  width: '100%',
  position: 'relative' as const,
  boxShadow: vars.form.fieldInsetShadow,
  backdropFilter: vars.surface.liquidGlass.backdropFilter.light,
  '@media': {
    [reducedMotion]: {
      transition: 'none',
    },
  },
} as const;

export const textareaRecipe = recipe({
  base: textareaBase,

  variants: {
    textareaSize: {
      xs: {
        padding: vars.spacing.padding.sm,
        fontSize: vars.typography.fontSize.xs,
        borderRadius: vars.spacing.borderRadius.sm,
        // 폰 — 16px 미만이면 iOS 가 포커스 때 화면을 확대한다(atoms/__tests__/mobileInputZoom.test.ts)
        '@media': { [mobile]: { fontSize: vars.typography.fontSize.body } },
      },
      sm: {
        padding: '10px 16px',
        fontSize: vars.typography.fontSize.sm,
        '@media': { [mobile]: { fontSize: vars.typography.fontSize.body } },
      },
      md: {
        padding: '14px 20px',
        fontSize: vars.typography.fontSize.base,
        '@media': { [mobile]: { fontSize: vars.typography.fontSize.body } },
      },
      lg: { padding: '18px 24px', fontSize: vars.typography.fontSize.md },
    },
    variant: {
      default: {},
      error: {
        borderColor: vars.bright.status.error,
      },
      success: {
        borderColor: vars.bright.status.success,
      },
    },
    isFocused: {
      true: {
        borderColor: vars.bright.primary.default,
        boxShadow: `${vars.form.fieldInsetShadow}, 0 0 12px ${vars.surface.highlight.medium}`,
      },
    },
    isDisabled: {
      true: {
        opacity: 0.4,
        cursor: 'not-allowed',
        background: vars.surface.card.disabled,
        boxShadow: vars.form.fieldDisabledShadow,
      },
    },
    resize: {
      none: { resize: 'none' },
      vertical: { resize: 'vertical' },
      both: { resize: 'both' },
    },
  },

  defaultVariants: {
    textareaSize: 'md',
    variant: 'default',
    isFocused: false,
    isDisabled: false,
    resize: 'vertical',
  },
});

// Hover on wrapper -> style the textarea (only when not disabled)
globalStyle(`${wrapper}:hover textarea:not(:disabled)`, {
  borderColor: vars.surface.border.accent,
});

// Placeholder styles
globalStyle(`${wrapper} textarea::placeholder`, {
  color: vars.typography.textColor.tertiary,
  opacity: 1,
  transition: `opacity ${vars.animation.duration.normal} ${vars.animation.easing.easeOut}, transform ${vars.animation.duration.normal} ${vars.animation.easing.easeOut}`,
  '@media': { [reducedMotion]: { transition: 'none' } },
});

globalStyle(`${wrapper} textarea:focus::placeholder`, {
  opacity: 0.5,
});

globalStyle(`${wrapper} textarea:focus-visible`, {
  outline: `${vars.focus.ring.width} ${vars.focus.ring.style} ${vars.focus.ring.color}`,
  outlineOffset: vars.focus.ring.offset,
  borderColor: vars.bright.primary.default,
  boxShadow: `${vars.form.fieldInsetShadow}, 0 0 12px ${vars.surface.highlight.medium}`,
});

// ============================================
// ERROR MESSAGE
// ============================================

export const errorMessage = style({
  fontFamily: vars.typography.fontFamily.primary,
  fontSize: vars.typography.fontSize.xs,
  fontWeight: vars.typography.fontWeight.medium,
  color: vars.bright.status.error,
  marginTop: vars.spacing.gap.xs,
  letterSpacing: vars.typography.letterSpacing.wide,
});
